import { safeSessionGet, safeSessionRemove, safeSessionSet } from "@/lib/browser-storage";

/**
 * The seeker's country (ISO-3166-1 alpha-2), resolved in the BROWSER.
 *
 * WHY CLIENT-SIDE AND NOT SSR: every acquisition surface that needs geo (report
 * landing pages, the kundali result, chat) is statically rendered and served
 * from the Cloudflare CDN — one cached HTML document shared by every visitor.
 * Branching on geo during render would bake one visitor's country into the
 * document the next visitor gets (the edge cache-coherence incident class), so
 * geo is resolved AFTER hydration and only ever swaps client-rendered values.
 *
 * SOURCE: `/cdn-cgi/trace`, Cloudflare's built-in same-origin endpoint. No
 * third-party geo-IP vendor, no API key, no extra DNS leg, and it is already on
 * the origin we serve from. Its `loc=` line is the same country Cloudflare would
 * put in a `CF-IPCountry` header.
 *
 * FAILS OPEN TO "IN" — always, on every error path (offline, timeout, blocked by
 * an extension, an unexpected body, SSR). India is the home market and its
 * pricing is the base price, so a failed lookup can only ever under-charge a
 * diaspora buyer; it can never show an Indian seeker a ₹999 price meant for a
 * dollar-anchored one. That asymmetry is the whole reason the default is "IN"
 * rather than "unknown".
 */

declare global {
  interface Window {
    /** Optional geo hint (ISO2) set by the edge; absent is normal. */
    __ishvaramGeoCountry?: string;
  }
}

const SESSION_KEY = "ish_geo_country";
const TRACE_URL = "/cdn-cgi/trace";
const TRACE_TIMEOUT_MS = 800;

/**
 * The fail-open default: the home market, whose price is the base price.
 * @internal
 */
export const DEFAULT_COUNTRY = "IN";

/** In-flight resolve, so N simultaneous callers share ONE network request. */
let inFlight: Promise<string> | null = null;

/**
 * Cloudflare emits `loc=XX` for addresses it cannot place (and `T1` for Tor).
 * Those are "unknown", NOT countries — treating them as a country would send an
 * unplaceable visitor down the diaspora path on a guess.
 */
function normalizeCountry(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const code = raw.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return null;
  if (code === "XX" || code === "T1") return null;
  return code;
}

function cache(country: string): void {
  safeSessionSet(SESSION_KEY, country);
  if (typeof window !== "undefined") window.__ishvaramGeoCountry = country;
}

/**
 * The country resolved EARLIER this session, or null if it is not known yet.
 *
 * Synchronous and allocation-cheap: this is what render paths call. `null` means
 * "not resolved yet" and every caller must treat it as India pricing — never
 * block, never suspend, never spin waiting for geo.
 */
export function getCachedCountry(): string | null {
  if (typeof window === "undefined") return null;
  return (
    normalizeCountry(window.__ishvaramGeoCountry) ?? normalizeCountry(safeSessionGet(SESSION_KEY))
  );
}

function parseTraceCountry(body: string): string | null {
  for (const line of body.split("\n")) {
    if (line.startsWith("loc=")) return normalizeCountry(line.slice(4));
  }
  return null;
}

/**
 * Resolve the country once per session, caching the result.
 *
 * Only a SUCCESSFUL lookup is cached. A transient failure returns "IN" without
 * poisoning the session: a diaspora seeker whose first request raced a flaky
 * network would otherwise be pinned to India pricing for their whole visit. The
 * `inFlight` promise is what keeps that retry from becoming a request storm.
 *
 * Never rejects.
 */
export function ensureGeoCountry(): Promise<string> {
  const cached = getCachedCountry();
  if (cached) return Promise.resolve(cached);
  if (typeof window === "undefined" || typeof fetch !== "function") {
    return Promise.resolve(DEFAULT_COUNTRY);
  }
  if (inFlight) return inFlight;

  inFlight = (async () => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TRACE_TIMEOUT_MS);
    try {
      const res = await fetch(TRACE_URL, {
        signal: controller.signal,
        cache: "no-store",
        credentials: "omit",
      });
      if (!res.ok) return DEFAULT_COUNTRY;
      const country = parseTraceCountry(await res.text());
      if (!country) return DEFAULT_COUNTRY;
      cache(country);
      return country;
    } catch {
      return DEFAULT_COUNTRY;
    } finally {
      clearTimeout(timer);
      inFlight = null;
    }
  })();

  return inFlight;
}

/**
 * Test seam: drop all resolved state (module-level + session + window).
 * @internal
 */
export function __resetGeoForTests(): void {
  inFlight = null;
  if (typeof window !== "undefined") delete window.__ishvaramGeoCountry;
  safeSessionRemove(SESSION_KEY);
}
